import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Auth from "@/pages/Auth";
import BuyerLayout from "@/pages/buyer/BuyerLayout";
import BuyerHome from "@/pages/buyer/BuyerHome";
import ShopDetail from "@/pages/buyer/ShopDetail";
import ProductDetail from "@/pages/buyer/ProductDetail";
import Checkout from "@/pages/buyer/Checkout";
import MyOrders from "@/pages/buyer/MyOrders";
import Profile from "@/pages/buyer/Profile";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import DriverDashboard from "@/pages/driver/DriverDashboard";
import WhatsAppConnection from "@/pages/admin/WhatsAppConnection";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import UsersManagement from "@/pages/admin/UsersManagement";
import ShopsManagement from "@/pages/admin/ShopsManagement";
import ProductsManagement from "@/pages/admin/ProductsManagement";
import OrdersManagement from "@/pages/admin/OrdersManagement";
import Settings from "@/pages/admin/Settings";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function Guard({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/auth" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Landing />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "seller") return <Navigate to="/seller" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  return <Navigate to="/app" replace />;
}

function App() {
  return (
    <div className="App" dir="rtl">
      <AuthProvider>
        <CartProvider>
          <Toaster position="bottom-left" richColors dir="rtl" />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/about" element={<About />} />
              <Route path="/auth" element={<Auth />} />

              <Route path="/app" element={<Guard role="buyer"><BuyerLayout /></Guard>}>
                <Route index element={<BuyerHome />} />
                <Route path="shop/:id" element={<ShopDetail />} />
                <Route path="product/:id" element={<ProductDetail />} />
                <Route path="checkout" element={<Checkout />} />
                <Route path="orders" element={<MyOrders />} />
                <Route path="profile" element={<Profile />} />
              </Route>

              <Route path="/seller" element={<Guard role="seller"><SellerDashboard /></Guard>} />
              <Route path="/driver" element={<Guard role="driver"><DriverDashboard /></Guard>} />
              <Route path="/admin/whatsapp" element={<AdminGuard><WhatsAppConnection /></AdminGuard>} />
              <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
              <Route path="/admin/users" element={<AdminGuard><UsersManagement /></AdminGuard>} />
              <Route path="/admin/shops" element={<AdminGuard><ShopsManagement /></AdminGuard>} />
              <Route path="/admin/products" element={<AdminGuard><ProductsManagement /></AdminGuard>} />
              <Route path="/admin/orders" element={<AdminGuard><OrdersManagement /></AdminGuard>} />
              <Route path="/admin/settings" element={<AdminGuard><Settings /></AdminGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
